import { type ApolloClient } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import type {
	ReorderGroupItemsMutation,
	ReorderGroupItemsMutationVariables,
	PatchProjectGroupMutation,
	PatchProjectGroupMutationVariables,
	DeleteProjectItemsMutation,
	DeleteProjectItemsMutationVariables,
	DeleteProjectSubItemsMutation,
	DeleteProjectSubItemsMutationVariables,
	AddProductToProjectsMutation,
	AddProductToProjectsMutationVariables,
	ProjectDetailsGroupFieldsFragment,
	ChangeCartItemFinishMutation,
	ChangeCartItemFinishMutationVariables
} from '../../__generated__/graphql-client-types';
import { modifyPendingItems } from '../../helpers/drag-and-drop/projects-drag-and-drop.helpers';
import {
	REORDER_GROUP_ITEMS,
	PATCH_PROJECT_GROUP,
	DELETE_PROJECT_ITEMS,
	DELETE_PROJECT_SUBITEMS,
	PROJECT_DETAILS_PAGE,
	CHANGE_CART_ITEM_FINISH
} from '../../queries/project/project-details.queries';
import { ADD_PRODUCT_TO_PROJECTS } from '../../queries/project/project.queries';
import type { MoveToPosition, ProjectDetailsGroup, ProductUpdatePayload } from '../../types/project.types';
import { useProjectDetails, useProjectId } from './project-details.hooks';

export const useProjectGroups = (): ProjectDetailsGroup[] => {
	const { project } = useProjectDetails();
	return project?.groups || [];
};

export const useProjectGroupMutations = () => {
	const projectId = useProjectId();
	const groups = useProjectGroups();
	const { project } = useProjectDetails();
	const [reorderGroupItems] = useMutation<ReorderGroupItemsMutation, ReorderGroupItemsMutationVariables>(REORDER_GROUP_ITEMS);
	const [patchProjectGroup] = useMutation<PatchProjectGroupMutation, PatchProjectGroupMutationVariables>(PATCH_PROJECT_GROUP);
	const [deleteProjectItems] = useMutation<DeleteProjectItemsMutation, DeleteProjectItemsMutationVariables>(DELETE_PROJECT_ITEMS);
	const [deleteProjectSubItem] = useMutation<DeleteProjectSubItemsMutation, DeleteProjectSubItemsMutationVariables>(
		DELETE_PROJECT_SUBITEMS
	);
	const [addProductToProjects] = useMutation<AddProductToProjectsMutation, AddProductToProjectsMutationVariables>(
		ADD_PRODUCT_TO_PROJECTS
	);
	const [changeCartItemFinish, { loading: isUpdatingProductOptions }] = useMutation<
		ChangeCartItemFinishMutation,
		ChangeCartItemFinishMutationVariables
	>(CHANGE_CART_ITEM_FINISH);

	const moveGroup = async (groupId: number, ordinal: number) => {
		const groupName = groups.find((g) => g.id === groupId)?.name;
		await patchProjectGroup({ variables: { input: { projectId, groupId, displayOrder: ordinal, name: groupName } } });
	};

	const reorderItem = async (groupId: number, targetOrdinal: number, itemIds: number[]) => {
		await reorderGroupItems({ variables: { input: { projectId, groupId, targetOrdinal, itemIds } } });
	};

	const getFinishIndex = (index: number, numGroups: number, position: MoveToPosition): number => {
		switch (position) {
			case 'up':
				return index - 1;
			case 'down':
				return index + 1;
			case 'top':
				return 0;
			case 'bottom':
				return numGroups - 1;
		}
	};

	const moveGroupPosition = async (
		index: number,
		position: MoveToPosition,
		updateGroupState: (projectGroups: ProjectDetailsGroupFieldsFragment[]) => void,
		disableDrag: (isDisabled: boolean) => void
	) => {
		disableDrag(true);
		const finishIndex = getFinishIndex(index, groups.length, position);
		const reorderedGroups = reorder({
			list: groups,
			startIndex: index,
			finishIndex
		});

		updateGroupState(reorderedGroups);

		try {
			await moveGroup(groups[index].id, finishIndex + 1);
		} finally {
			disableDrag(false);
		}
	};

	const moveItemPosition = async (
		itemId: number,
		groupId: number,
		itemIndex: number,
		position: MoveToPosition,
		updateGroupState: (projectGroups: ProjectDetailsGroupFieldsFragment[]) => void,
		disableDrag: (isDisabled: boolean) => void
	) => {
		disableDrag(true);
		const groupsCopy = [...groups];
		const groupIndex = groups.findIndex((group) => group.id === groupId);
		const pendingItems = groups[groupIndex].pending ?? [];
		const finishIndex = getFinishIndex(itemIndex, pendingItems.length, position);
		const reorderedItems = reorder({
			list: pendingItems,
			startIndex: itemIndex,
			finishIndex
		});

		const newGroups = modifyPendingItems(groupsCopy, groupIndex, reorderedItems);

		updateGroupState(newGroups);

		try {
			await reorderItem(groupId, finishIndex, [itemId]);
		} finally {
			disableDrag(false);
		}
	};

	return {
		moveGroupPosition,
		moveGroup,
		moveItemPosition,
		reorderItem,
		isUpdatingProductOptions,
		updateGroupName: (groupId: number, name: string) => {
			void patchProjectGroup({ variables: { input: { projectId, groupId, name } } });
		},
		deleteGroupItem: async (itemId: number, groupId: number) => {
			const cartId = groups.find((group) => group.id === groupId)?.cartId;
			await deleteProjectItems({
				variables: {
					input: {
						projectId,
						items: [
							{
								cartId: Number(cartId),
								itemId
							}
						]
					}
				}
			});
		},
		deleteSubItem: async (itemId: number, groupId: number, subItemId: number, isSingleCartProject: boolean) => {
			const cartId = groups.find((group) => group.id === groupId)?.cartId;
			await deleteProjectSubItem({
				variables: {
					input: {
						projectId,
						isSingleCartProject,
						groupId,
						items: [
							{
								cartId: Number(cartId),
								itemId,
								subItemId
							}
						]
					}
				}
			});
		},
		addProductToProject: (
			groupId: number,
			variantId: number,
			quantity: number,
			parentItemId?: number,
			ordinal?: number
		): Promise<ApolloClient.MutateResult<AddProductToProjectsMutation>> => {
			const cartId = groups.find((group) => group.id === groupId)?.cartId;
			return addProductToProjects({
				variables: {
					input: {
						projects: [
							{
								cartId,
								projectId,
								quantity,
								groupId: project?.cartId ? groupId : cartId
							}
						],
						addToCartInput: {
							pricedOptions: [],
							subItems: [],
							variantId,
							parentItemId,
							ordinal
						}
					}
				},
				refetchQueries: [{ query: PROJECT_DETAILS_PAGE, variables: { projectId } }]
			});
		},
		deleteItem: (itemId: number, groupId: number) => {
			const cartId = groups.find((group) => group.id === groupId)?.cartId;
			void deleteProjectItems({
				variables: {
					input: {
						projectId,
						items: [
							{
								cartId: Number(cartId),
								itemId
							}
						]
					}
				}
			});
		},
		updateProductOptions: async (productInfo: ProductUpdatePayload, newQuantity: number, newFinishId: number) => {
			const { itemId, parentItemId, cartId, groupId } = productInfo;
			const subItemId = parentItemId ? itemId : undefined;

			await changeCartItemFinish({
				variables: {
					input: {
						projectId,
						cartId,
						itemId: parentItemId ?? itemId,
						subItemId,
						quantity: newQuantity,
						productVariantId: newFinishId,
						groupId,
						isSingleCartProject: Boolean(project?.cartId)
					}
				}
			});
		}
	};
};
